import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  LinearProgress,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';

import { approveCreativeTask, uploadCreativeTaskFiles } from '@src/api/creativeStudio';

// Designers name deliverables after the task: "FLT-0085-T1728_9x16_v1.mp4".
// 85% of files in production carry their own task code and 97% carry a
// code-shaped token, so matching a dropped folder to tasks is near-automatic —
// which is what turns "open 20 task panels" into one screen.
const TASK_CODE_RE = /(FLT-\d+-T\d+|TASK-\d+)/i;

const UPLOAD_CONCURRENCY = 4;

const errorMessage = (err, fallback) =>
  err?.response?.data?.detail || err?.message || fallback;

function matchFileToTask(file, tasks) {
  const found = (file.name || '').match(TASK_CODE_RE);
  if (!found) return null;
  const code = found[1].toUpperCase();
  return tasks.find((t) => (t.task_code || '').toUpperCase() === code) || null;
}

/** Run `worker` over items with a small parallel pool, preserving order. */
async function pooled(items, limit, worker) {
  const results = new Array(items.length);
  let cursor = 0;
  const runners = Array.from({ length: Math.min(limit, items.length) }, async () => {
    while (cursor < items.length) {
      const index = cursor;
      cursor += 1;
      results[index] = await worker(items[index], index);
    }
  });
  await Promise.all(runners);
  return results;
}

/**
 * Drop a whole folder of Frame.io downloads onto a flight at once.
 *
 * Each file is matched to its task by the code in its filename; anything that
 * doesn't match gets a picker instead of being silently dropped. Uploads run a
 * few at a time so twenty files are one action, not twenty.
 */
export default function BulkDeliverableUploadDialog({ open, tasks = [], onClose, onDone }) {
  const [rows, setRows] = useState([]);
  const [dragActive, setDragActive] = useState(false);
  const [approveOnUpload, setApproveOnUpload] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  // Only tasks that can still receive a deliverable.
  const candidates = useMemo(
    () => tasks.filter((t) => ['assigned', 'in_progress', 'submitted', 'needs_revision', 'approved'].includes(t.status)),
    [tasks],
  );

  const existingFileCount = (taskId) => {
    const task = candidates.find((t) => t.id === taskId);
    return task ? (task.file_count || 0) : 0;
  };

  const addFiles = (fileList) => {
    const incoming = Array.from(fileList || []);
    if (!incoming.length) return;
    setDone(false);
    setRows((prev) => {
      const seen = new Set(prev.map((r) => `${r.file.name}:${r.file.size}`));
      const fresh = incoming
        .filter((f) => !seen.has(`${f.name}:${f.size}`))
        .map((file) => {
          const task = matchFileToTask(file, candidates);
          return {
            file,
            taskId: task ? task.id : '',
            autoMatched: Boolean(task),
            status: 'pending',
            error: '',
          };
        });
      return [...prev, ...fresh];
    });
  };

  // Anything needing a human decision sorts first — an unmatched file or one
  // landing on a task that already holds a deliverable. Auto-matched rows are
  // the boring majority and can settle underneath.
  const needsAttention = (row) => !row.taskId || Boolean(existingFileCount(row.taskId));
  const ordered = useMemo(() => {
    const indexed = rows.map((row, index) => ({ row, index }));
    return [
      ...indexed.filter(({ row }) => needsAttention(row)),
      ...indexed.filter(({ row }) => !needsAttention(row)),
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, candidates]);

  const matched = rows.filter((r) => r.taskId);
  const unmatched = rows.filter((r) => !r.taskId);
  const succeeded = rows.filter((r) => r.status === 'done');
  const failed = rows.filter((r) => r.status === 'failed');

  const handleUpload = async () => {
    setUploading(true);
    setError('');
    const targets = rows
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => row.taskId && row.status !== 'done');

    await pooled(targets, UPLOAD_CONCURRENCY, async ({ row, index }) => {
      setRows((prev) => prev.map((r, i) => (i === index ? { ...r, status: 'uploading' } : r)));
      try {
        await uploadCreativeTaskFiles(row.taskId, [row.file]);
        if (approveOnUpload) {
          const task = candidates.find((t) => t.id === row.taskId);
          // Only advance tasks that aren't already approved.
          if (task && task.status !== 'approved') {
            await approveCreativeTask(row.taskId, {});
          }
        }
        setRows((prev) => prev.map((r, i) => (i === index ? { ...r, status: 'done' } : r)));
      } catch (err) {
        const message = errorMessage(err, 'Upload failed');
        setRows((prev) => prev.map((r, i) => (
          i === index ? { ...r, status: 'failed', error: message } : r
        )));
      }
    });

    setUploading(false);
    setDone(true);
    onDone?.();
  };

  const handleClose = () => {
    setRows([]);
    setDone(false);
    setError('');
    onClose?.();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>Upload deliverables</DialogTitle>
      <DialogContent dividers>
        {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}

        <Box
          onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={() => setDragActive(false)}
          onDrop={(e) => { e.preventDefault(); setDragActive(false); addFiles(e.dataTransfer?.files); }}
          sx={{
            p: 3,
            mb: 2,
            border: '2px dashed',
            borderColor: dragActive ? 'primary.main' : 'divider',
            bgcolor: dragActive ? 'primary.50' : 'transparent',
            borderRadius: 1,
            textAlign: 'center',
          }}
        >
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Drop your whole Frame.io download here — files are matched to tasks by the
            code in the filename.
          </Typography>
          <Button variant="outlined" component="label" size="small">
            Choose files
            <input
              type="file"
              hidden
              multiple
              onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
            />
          </Button>
        </Box>

        {rows.length ? (
          <>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Chip size="small" color="success" label={`${matched.length} matched`} />
              {unmatched.length ? (
                <Chip size="small" color="warning" label={`${unmatched.length} need a task`} />
              ) : null}
              <Box sx={{ flexGrow: 1 }} />
              <FormControlLabel
                control={(
                  <Checkbox
                    size="small"
                    checked={approveOnUpload}
                    onChange={(e) => setApproveOnUpload(e.target.checked)}
                  />
                )}
                label={<Typography variant="caption">Mark tasks approved after upload</Typography>}
              />
            </Stack>

            {uploading ? <LinearProgress sx={{ mb: 1 }} /> : null}

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>File</TableCell>
                  <TableCell sx={{ width: 260 }}>Task</TableCell>
                  <TableCell sx={{ width: 90 }}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ordered.map(({ row, index }) => (
                  <TableRow key={`${row.file.name}-${index}`}>
                    <TableCell>
                      <Typography variant="caption" noWrap>{row.file.name}</Typography>
                      {row.taskId && existingFileCount(row.taskId) ? (
                        <Typography variant="caption" color="warning.main" sx={{ display: 'block' }}>
                          This task already has {existingFileCount(row.taskId)} file
                          {existingFileCount(row.taskId) === 1 ? '' : 's'} — the newest video is the one that ships.
                        </Typography>
                      ) : null}
                    </TableCell>
                    <TableCell>
                      <TextField
                        select
                        size="small"
                        fullWidth
                        variant="standard"
                        value={row.taskId}
                        onChange={(e) => setRows((prev) => prev.map((r, i) => (
                          i === index ? { ...r, taskId: e.target.value, autoMatched: false } : r
                        )))}
                      >
                        <MenuItem value=""><em>— skip this file —</em></MenuItem>
                        {candidates.map((t) => (
                          <MenuItem key={t.id} value={t.id}>
                            {t.task_code || `#${t.id}`}
                          </MenuItem>
                        ))}
                      </TextField>
                    </TableCell>
                    <TableCell>
                      {row.status === 'uploading' ? <CircularProgress size={13} /> : null}
                      {row.status === 'done' ? <CheckCircleIcon sx={{ fontSize: 16, color: '#2e7d32' }} /> : null}
                      {row.status === 'failed' ? (
                        <Chip size="small" label={row.error.slice(0, 40)} sx={{ height: 18, fontSize: '0.6rem', bgcolor: '#ffcdd2', color: '#c62828' }} />
                      ) : null}
                      {row.status === 'pending' ? (
                        row.autoMatched
                          ? <Chip size="small" label="auto" sx={{ height: 18, fontSize: '0.6rem' }} />
                          : <HelpOutlineIcon sx={{ fontSize: 15, color: 'text.disabled' }} />
                      ) : null}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {done ? (
              <Alert severity={failed.length ? 'warning' : 'success'} sx={{ mt: 2 }} icon={failed.length ? <ErrorOutlineIcon fontSize="inherit" /> : undefined}>
                {succeeded.length} uploaded{failed.length ? `, ${failed.length} failed — fix and upload again` : ''}.
                {!failed.length ? ' Select the tasks and launch them to Facebook.' : ''}
              </Alert>
            ) : null}
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{done ? 'Done' : 'Cancel'}</Button>
        <Button
          variant="contained"
          disabled={uploading || !matched.filter((r) => r.status !== 'done').length}
          onClick={handleUpload}
        >
          {uploading
            ? 'Uploading…'
            : `Upload ${matched.filter((r) => r.status !== 'done').length} file${matched.filter((r) => r.status !== 'done').length === 1 ? '' : 's'}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
